import { useEffect, RefObject } from 'react';
import { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';
import { CanvasLogger } from '../../../../../../utils/loggers/CanvasLogger';

export interface WasmInitializationDependencies {
  skipInitialization: boolean;
  sharedWasmClient: TypesetterWorkerClient | null | undefined;
  typesetterClientRef: RefObject<TypesetterWorkerClient | null>;
  setTypesetterClient: (client: TypesetterWorkerClient | null) => void;
  setIsTypesetterReady: (ready: boolean) => void;
  setRenderTrigger: (updater: (prev: number) => number) => void;
  onWasmStatusChange?: (status: 'loading' | 'ready' | 'error') => void;
  renderTextRef: RefObject<(() => Promise<void>) | null>;
}

/**
 * Initializes the WASM typesetter client with proper lifecycle management.
 * Handles shared worker setup, client creation, glyph cache initialization,
 * and cleanup on component unmount.
 */
export function setupWasmInitialization({
  skipInitialization,
  sharedWasmClient,
  typesetterClientRef,
  setTypesetterClient,
  setIsTypesetterReady,
  setRenderTrigger,
  onWasmStatusChange,
  renderTextRef
}: WasmInitializationDependencies) {
  return useEffect(() => {
    const initTypesetter = async () => {
      CanvasLogger.wasm.initStart();
      CanvasLogger.wasm.initConfig(skipInitialization, !!sharedWasmClient);

      // PHASE 2.2: Use shared worker if provided
      if (skipInitialization && sharedWasmClient) {
        CanvasLogger.wasm.usingSharedWorker('Phase 2.2');
        typesetterClientRef.current = sharedWasmClient;
        setTypesetterClient(sharedWasmClient);
        setIsTypesetterReady(true);
        onWasmStatusChange?.('ready');
        CanvasLogger.wasm.sharedWorkerReady();
        return;
      }

      // Signal loading state
      onWasmStatusChange?.('loading');

      try {
        CanvasLogger.wasm.init.start();

        // Initialize glyph operations cache
        (window as any).__edg_glyphOps = (window as any).__edg_glyphOps || new Map();
        CanvasLogger.glyph.warm('__edg_glyphOps', 0, { action: 'cache_initialized' });

        CanvasLogger.pipeline.start('TypesetterWorkerClient creation');
        const client = new TypesetterWorkerClient({
          onReady: () => {
            CanvasLogger.wasm.ready();
            setIsTypesetterReady(true);
            // Signal ready state
            onWasmStatusChange?.('ready');
          },
          onError: (error) => {
            CanvasLogger.wasm.error(error instanceof Error ? error.message : String(error));
            // Signal error state
            onWasmStatusChange?.('error');
          },
          onLayoutComplete: (msg) => {
            CanvasLogger.layout.calculate.perf(0, { message: msg });
            // Trigger re-render when layout is complete
            renderTextRef.current?.();
          }
        });
        CanvasLogger.pipeline.complete('TypesetterWorkerClient creation', 0);

        CanvasLogger.pipeline.start('client.initialize()');
        try {
          await client.initialize();
          CanvasLogger.pipeline.complete('client.initialize()', 0);
        } catch (initError) {
          CanvasLogger.pipeline.error('client.initialize()', initError instanceof Error ? initError.message : String(initError));
          throw initError; // Re-throw to be caught by outer try-catch
        }

        // Set BOTH ref (for cleanup) and state (for React reactivity)
        typesetterClientRef.current = client;
        setTypesetterClient(client);
        CanvasLogger.lifecycle.mount('TypesetterClient', { refSet: true, stateSet: true });

        // Set up glyph cache callback for incremental re-renders
        client.setGlyphCacheCallback(() => {
          CanvasLogger.renderCycle.incremental(0, { reason: 'glyph_cache_callback' });
          // Trigger a re-render by updating the render trigger
          setRenderTrigger(prev => prev + 1);
        });
        CanvasLogger.pipeline.complete('glyph_cache_callback_setup', 0);

        CanvasLogger.pipeline.complete('TYPESETTER_INITIALIZATION', 0);

      } catch (error) {
        CanvasLogger.wasm.error(error instanceof Error ? error.message : String(error), {
          stack: error instanceof Error ? error.stack : 'No stack trace',
          type: typeof error,
          constructor: error?.constructor?.name
        });
        // Signal error state
        onWasmStatusChange?.('error');
      }
    };

    initTypesetter();

    return () => {
      // PHASE 2.2: Don't terminate shared worker
      if (skipInitialization && sharedWasmClient) {
        CanvasLogger.wasm.cleanupSkipped('shared worker');
        return;
      }

      CanvasLogger.wasm.cleanup.complete({ action: 'terminating' });
      if (typesetterClientRef.current) {
        CanvasLogger.wasm.cleanup.complete({ action: 'terminate_called', refExists: true });
        typesetterClientRef.current.terminate();
        typesetterClientRef.current = null;
        // Note: Can't call setTypesetterClient(null) here as component is unmounting
        CanvasLogger.lifecycle.unmount('TypesetterClient', { terminated: true });
      } else {
        CanvasLogger.wasm.cleanup.complete({ action: 'no_worker', refExists: false });
      }
    };
  }, [skipInitialization, sharedWasmClient]);
}
